import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import {
    createColumnHelper,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable
} from '@tanstack/react-table'
import { showPopAlert } from '../PopAlert'
import { getValueOfSpecifiedKey, addNoColumn, addCheckboxColumn, addRadioColumn } from './utils'
import { View } from './View'

export const columnHelper = createColumnHelper()

/**
 * <a href="https://tanstack.com/table/latest/docs/introduction" target="_blank">TanStack Table</a>を利用したIBA独自コンポーネントです<br>
 */
export const Table = ({
    defaultSelectedData,
    selectType,
    selectedId,
    message,
    maxCheck,
    columns,
    isRowNumber,
    rowNumberColumnName,
    data,
    rowCount,
    noDataText,
    pagination,
    pageSieList,
    sort,
    handleChangePagination,
    handleChangeSort,
    enableMultiSort,
    manualPagination,
    manualSorting,
    isAllCheck,
    changeSelectedDataType,
    handleClickSelectBox,
    handleClickRow,
    onChangeSelectedData,
    disabledCheckboxList,
    ...props
}) => {
    const dataList = pageSieList.map((value) => {
        return { value, content: `${value} / Page` }
    })
    const [pager, setPager] = useState(pagination)
    const [selectInputValue, setSelectInputValue] = useState(
        getValueOfSpecifiedKey(dataList, 'value', pagination.pageSize, 'content')
    )
    const [sorting, setSorting] = useState(sort)
    const [selectedData, setSelectedData] = useState(defaultSelectedData)

    /**
     * selectedDataにクリックしたデータを追加または削除
     * @param {string} clickData
     * @returns {void}
     */
    const handleChangeSelectedData = (clickData) => {
        if (selectType === 'checkbox') {
            setSelectedData((prevSelectedIpcCodeList) => {
                let updater = [...prevSelectedIpcCodeList]
                if (updater.includes(clickData)) {
                    updater = updater.filter((item) => {
                        return item !== clickData
                    })
                } else {
                    if (!isAllCheck && message && updater.length >= maxCheck) {
                        showPopAlert({
                            message,
                            status: 'warning',
                            timer: 5000
                        })
                    } else {
                        updater.push(clickData)
                    }
                }
                return updater
            })
        } else if (selectType === 'radio') {
            setSelectedData(clickData)
        }
    }

    const [isAll, setIsAll] = useState(false)
    /**
     * 全選択チェックボックスの状態でselectedDataに全データ追加または削除
     * @param {string} value
     * @returns {void}
     */
    const handleChangeAllCheckbox = (value) => {
        if (value) {
            const allData = data.map((item) => {
                return item[selectedId]
            })
            const filterAllData = allData.filter((item) => {
                return !disabledCheckboxList?.includes(item)
            })
            setSelectedData(filterAllData)
        } else {
            setSelectedData([])
        }
    }

    /**
     * チェックボックスまたはラジオボタンクリック時に発火
     * @param {string} clickData
     * @returns {void}
     */
    const onClickSelectBox = (clickData, e) => {
        if (changeSelectedDataType === 'selectBox') {
            handleChangeSelectedData(clickData)
        }
        handleClickSelectBox && handleClickSelectBox(clickData, e)
    }

    /**
     * 行選択時に発火
     * @param {Array<string | number>} original
     * @returns {void}
     */
    const onClickRow = (original) => {
        const clickData = original[selectedId]
        if (changeSelectedDataType === 'row') {
            handleChangeSelectedData(clickData)
        }
        handleClickRow && handleClickRow(original)
    }

    if (isRowNumber) {
        columns = addNoColumn(rowNumberColumnName, columns)
    }

    if (selectType === 'checkbox') {
        columns = addCheckboxColumn(
            data,
            columns,
            selectedId,
            selectedData,
            isAllCheck,
            isAll,
            onClickSelectBox,
            handleChangeAllCheckbox,
            disabledCheckboxList
        )
    } else if (selectType === 'radio') {
        columns = addRadioColumn(columns, selectedId, selectedData, onClickSelectBox)
    }

    const table = useReactTable({
        columns,
        data,
        rowCount: rowCount || data.length,
        // 必要な記述
        getCoreRowModel: getCoreRowModel(),
        // ページネーション
        getPaginationRowModel: getPaginationRowModel(),
        // ソート
        getSortedRowModel: getSortedRowModel(),
        // ページネーションが変更されたときに発火
        onPaginationChange: setPager,
        // ソートが変更されたときに発火
        onSortingChange: setSorting,
        enableMultiSort,
        manualPagination,
        manualSorting,
        state: {
            pagination: pager,
            sorting
        }
    })

    const handleChangePage = (page) => {
        // ページの変更
        table.setPageIndex(page - 1)
    }

    const handleChangePageSize = (itemKey) => {
        const pageSize = getValueOfSpecifiedKey(dataList, 'content', itemKey, 'value')
        setSelectInputValue(itemKey)
        // ページ表示数の変更
        table.setPageSize(pageSize)
    }

    useEffect(() => {
        if (sort) {
            setSorting(sort)
        }
    }, [sort])

    useEffect(() => {
        if (pagination) {
            table.setPageSize(pagination.pageSize)
            setSelectInputValue(getValueOfSpecifiedKey(dataList, 'value', pagination.pageSize, 'content'))
            table.setPageIndex(pagination.pageIndex)
        }
    }, [pagination])

    useEffect(() => {
        if (defaultSelectedData) {
            setSelectedData(defaultSelectedData)
        } else {
            const defaultValue = selectType === 'checkbox' ? [] : ''
            setSelectedData(defaultValue)
        }
    }, [defaultSelectedData])

    useEffect(() => {
        if (disabledCheckboxList) {
            setSelectedData([])
        }
    }, [disabledCheckboxList])

    useEffect(() => {
        onChangeSelectedData && onChangeSelectedData(selectedData)
    }, [selectedData])

    useEffect(() => {
        const disabledCheckNum = disabledCheckboxList ? disabledCheckboxList.length : 0
        const activeDataLength = data.length - disabledCheckNum
        if (data.length > 0 && activeDataLength > 0 && selectedData?.length === activeDataLength) {
            setIsAll(true)
        } else {
            setIsAll(false)
        }
    }, [data, selectedData])

    useEffect(() => {
        handleChangePagination && handleChangePagination(pager)
    }, [pager])

    useEffect(() => {
        handleChangeSort && handleChangeSort(sorting)
    }, [sorting])

    return (
        <View
            data={data}
            noDataText={noDataText}
            table={table}
            dataList={dataList}
            selectInputValue={selectInputValue}
            handleChangePage={handleChangePage}
            handleChangePageSize={handleChangePageSize}
            onClickRow={onClickRow}
            selectedId={selectedId}
            selectType={selectType}
            selectedData={selectedData}
            disabledCheckboxList={disabledCheckboxList}
            {...props}
        />
    )
}

Table.propTypes = {
    /**
     * カラムの定義<br>
     * <a href="https://tanstack.com/table/v8/docs/guide/column-defs">詳しい仕様</a><br>
     * よく使いそうなcreateColumnHelper()の第二引数のobject内に記載する値<br>
     * ソート関数の指定：sortingFn：(rowA: object, rowB: object, columnID: string) => number
     */
    columns: PropTypes.arrayOf(PropTypes.object),
    /**
     * テーブルに表示するデータ
     */
    data: PropTypes.arrayOf(PropTypes.object),
    /**
     * paginationをmanualで行う場合、トータルの件数を指定
     */
    rowCount: PropTypes.number,
    /**
     * データがないときに表示するテキスト
     */
    noDataText: PropTypes.string,
    /**
     * 行No.の表示
     */
    isRowNumber: PropTypes.bool,
    /**
     * 行No.のカラム名
     */
    rowNumberColumnName: PropTypes.string,
    /**
     * チェックボックスまたはラジオを表示する
     */
    selectType: PropTypes.oneOf(['checkbox', 'radio']),
    /**
     * 選択に使用するID<br>
     * ※dataのkeyから指定
     */
    selectedId: PropTypes.string,
    /**
     * selectTypeがcheckboxの場合、全選択・解除のチェックボックスを表示する<br>
     * ※個数制限をかける場合、falseにすること
     */
    isAllCheck: PropTypes.bool,
    /**
     * チェックボックスの選択個数上限の際にでるアラートのテキスト<br>
     * ※個数制限をかける際は必須
     */
    message: PropTypes.string,
    /**
     * チェックボックスの選択個数上限数を指定<br>
     */
    maxCheck: PropTypes.number,
    /**
     * データ選択をどのタイミングで行うか選択<br>
     * selectBox: チェックボックスまたはラジオをクリック<br>
     * row: 行をクリック
     */
    changeSelectedDataType: PropTypes.oneOf(['selectBox', 'row']),
    /**
     * チェックボックスまたはラジオボタンクリック時に発火<br>
     * (clickData: string | number, e: React.SyntheticEvent) => void
     */
    handleClickSelectBox: PropTypes.func,
    /**
     * 行クリック時に発火<br>
     * (original: object) => void
     */
    handleClickRow: PropTypes.func,
    /**
     * 選択しているデータが変更されたときに発火<br>
     * (selectedData: string | number | array\<string | number\>) => void
     */
    onChangeSelectedData: PropTypes.func,
    /**
     * デフォルトでチェックしておくチェックボックスのvalueを指定<br>
     * ※selectedIdで指定したkeyのvalue
     */
    defaultSelectedData: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))
    ]),
    /**
     * チェックボックス表示時、disabledにするチェックボックスのvalueを指定<br>
     * ※selectedIdで指定したkeyのvalue
     */
    disabledCheckboxList: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    /**
     * ヘッダーの表示
     */
    isHeader: PropTypes.bool,
    /**
     * ページャーを上に表示
     */
    isTopPagination: PropTypes.bool,
    /**
     * ページャーを下に表示
     */
    isBottomPagination: PropTypes.bool,
    /**
     * ページネーション
     */
    pagination: PropTypes.exact({
        pageIndex: PropTypes.number,
        pageSize: PropTypes.number
    }),
    /**
     * ページサイズのリスト
     */
    pageSieList: PropTypes.arrayOf(PropTypes.number),
    /**
     * ソート
     */
    sort: PropTypes.arrayOf(
        PropTypes.exact({
            id: PropTypes.string,
            desc: PropTypes.bool
        })
    ),
    /**
     * ページネーションが変更された時に発火する関数
     * (pager: object) => void
     */
    handleChangePagination: PropTypes.func,
    /**
     * ソートが変更された時に発火する関数
     * (sorting: array\<object\>) => void
     */
    handleChangeSort: PropTypes.func,
    /**
     * ダウンロードボタンの表示
     */
    isDownload: PropTypes.bool,
    /**
     * ダウンロード時にローディングを表示
     */
    isDownloadLoading: PropTypes.bool,
    /**
     * ダウンロードボタン押下時に発火する関数
     * (event: React.SyntheticEvent) => void
     */
    handleClickDownload: PropTypes.func,
    /**
     * マルチソートする
     */
    enableMultiSort: PropTypes.bool,
    /**
     * ページネーションをマニュアルで行う<br>
     * ※ページネーションをかけずにデータをすべて表示したいときもtrue
     */
    manualPagination: PropTypes.bool,
    /**
     * ソートをマニュアルで行う
     */
    manualSorting: PropTypes.bool,
    /**
     * テーブルの左上に表示されるコンテンツ
     */
    children: PropTypes.node,
    /**
     * テーブルを囲っているdivスタイル変更用のクラス名
     */
    tableWrapClass: PropTypes.any,
    /**
     * スタイル変更用のクラス名
     */
    className: PropTypes.any
}

Table.defaultProps = {
    noDataText: 'No Data',
    data: [],
    isHeader: true,
    isTopPagination: true,
    isBottomPagination: true,
    pagination: {
        pageIndex: 0,
        pageSize: 50
    },
    sort: [],
    pageSieList: [5, 10, 20, 25, 50, 100],
    isDownload: true,
    rowNumberColumnName: 'No'
}
